// API client for the RBAC surface (GYM-208 backend, GYM-210 frontend).
//
// Stays thin: type contracts + apiFetch wrappers. No caching or state — the
// TanStack Query hooks in lib/hooks/rbac.ts handle that. Splitting them keeps
// the contract testable in isolation and lets non-component code (e.g. an
// imperative bootstrap) call the API without pulling React Query in.

import { apiFetch } from "@/lib/api/client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// Mirrors the backend `Scope` enum (apps/accounts/permissions/scope.py).
// `own_members` is the trainer "own clients" scope (GYM-66) carried by many
// OWN_MEMBERS-scoped permissions; it MUST be listed here or the role form's
// grant validation silently rejects those grants (GYM-1208).
export type Scope = "tenant" | "branch" | "assigned" | "own_members";

export interface PermissionDef {
  code: string;
  label: string;
  description: string;
  category: string;
  scope_choices: Scope[];
}

export interface RoleGrant {
  code: string;
  scope: Scope;
}

export interface Role {
  id: string;
  name: string;
  is_system: boolean;
  grants: RoleGrant[];
  permission_count: number;
  user_count: number;
  /** GYM-1430: count of users in this role who have enabled 2FA. */
  enrolled_2fa_count: number;
  created_at: string;
  updated_at: string;
}

export interface RoleWritePayload {
  name: string;
  grants?: RoleGrant[];
}

export interface UserListEntry {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_superuser: boolean;
  tenant_id: string | null;
  branch_id: string | null;
  branch_name: string | null;
  locale: string;
  density: string;
  theme: string;
  role_ids: string[];
  /** GYM-1578: false = user created via invite path, hasn't set a password yet */
  has_usable_password: boolean;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface EffectiveGrant {
  code: string;
  scope: Scope;
}

export interface ListUsersParams {
  branchId?: string;
  page?: number;
  pageSize?: number;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export function listPermissions(): Promise<PermissionDef[]> {
  return apiFetch<PermissionDef[]>("/permissions/");
}

export function listRoles(): Promise<Role[]> {
  // ``/roles/`` returns a flat array (no pagination on the role list — bounded
  // by tenant size, typically <50 even for large tenants).
  return apiFetch<Role[]>("/roles/");
}

export function getRole(roleId: string): Promise<Role> {
  return apiFetch<Role>(`/roles/${roleId}/`);
}

export function assignUserRoles(userId: number, roleIds: string[]): Promise<{ roles: string[] }> {
  return apiFetch<{ roles: string[] }>(`/users/${userId}/`, {
    method: "PATCH",
    body: JSON.stringify({ roles: roleIds }),
  });
}

export function createRole(body: RoleWritePayload): Promise<Role> {
  return apiFetch<Role>("/roles/", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateRole(roleId: string, body: Partial<RoleWritePayload>): Promise<Role> {
  return apiFetch<Role>(`/roles/${roleId}/`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function deleteRole(roleId: string): Promise<void> {
  return apiFetch<void>(`/roles/${roleId}/`, { method: "DELETE" });
}

export function listUsers(params: ListUsersParams = {}): Promise<PaginatedResponse<UserListEntry>> {
  const search = new URLSearchParams();
  if (params.branchId) search.set("branch_id", params.branchId);
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("page_size", String(params.pageSize));
  const qs = search.toString();
  return apiFetch<PaginatedResponse<UserListEntry>>(`/users/${qs ? `?${qs}` : ""}`);
}

export function updateUserBranch(userId: number, branchId: string | null): Promise<UserListEntry> {
  return apiFetch<UserListEntry>(`/users/${userId}/`, {
    method: "PATCH",
    body: JSON.stringify({ branch_id: branchId }),
  });
}

export function getMyPermissions(): Promise<EffectiveGrant[]> {
  return apiFetch<EffectiveGrant[]>("/users/me/permissions/");
}

// GYM-47: branches feed the user-create form's branch select. The endpoint
// is ``GET /api/v1/tenants/branches/`` and is exposed by apps.tenants.
// Lives on the rbac client because the only consumer is the user-create
// form; if more domains start using it we'll lift it to its own module.
export interface BranchSummary {
  id: string;
  name: string;
  code: string;
  is_active: boolean;
}

export function listBranches(): Promise<BranchSummary[]> {
  return apiFetch<BranchSummary[]>("/tenants/branches/");
}

// GYM-47: admin-side user creation. Returns the freshly-created user in
// ``UserListEntry`` shape (matches the GET /users/ row) so callers can
// append it to their cached list directly.
export interface CreateTenantUserPayload {
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  branch_id?: string | null;
  role_ids?: string[];
  send_invite: boolean;
  password?: string;
  locale?: "en" | "ar";
}

export function createTenantUser(
  payload: CreateTenantUserPayload,
): Promise<UserListEntry> {
  return apiFetch<UserListEntry>("/users/", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// GYM-1578: re-issue invite email for a user who hasn't set a password yet.
export function resendUserInvite(userId: number): Promise<{ detail: string }> {
  return apiFetch<{ detail: string }>(`/users/${userId}/resend-invite/`, {
    method: "POST",
  });
}

// Admin force-set password for a tenant user (used from Staff Edit page).
export function adminSetPassword(userId: number, password: string): Promise<{ detail: string }> {
  return apiFetch<{ detail: string }>(`/users/${userId}/set-password/`, {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

// Admin update username for a tenant user (used from Staff Edit page).
export function adminSetUsername(userId: number, username: string): Promise<{ detail: string }> {
  return apiFetch<{ detail: string }>(`/users/${userId}/set-username/`, {
    method: "PATCH",
    body: JSON.stringify({ username }),
  });
}
